import { AxiosRequest } from 'src/lib/axios/Axios';

export interface PaymentCard {
  payment_card_id?: number;
  card_id?: number | string;
  id?: number;
  name?: string;
  card_name?: string;
  brand?: string;
  type?: string;
  promotion_id?: number | string;
  promotionId?: number | string;
  promotion_name?: string;
  promotionName?: string;
  [key: string]: unknown;
}

export interface OnePaymentPublication {
  publication_id: string | number;
  name: string;
  sku?: string;
  config_values?: Record<string, unknown>;
}

export interface OnePaymentPromotion {
  promotion_id: number;
  name: string;
}

export interface PublicationPaymentMethodsResult {
  publications: OnePaymentPublication[];
  paymentMethods: OnePaymentPromotion[];
}

type PaymentCardResponse =
  | PaymentCard[]
  | {
      data?: PaymentCard[];
      items?: PaymentCard[];
      paymentCards?: PaymentCard[];
      cards?: PaymentCard[];
    };

type PublicationPaymentMethodsResponse =
  | Record<string, unknown>[]
  | {
      data?: Record<string, unknown>[] | PublicationPaymentMethodsResult;
      publications?: Record<string, unknown>[];
      paymentMethods?: Record<string, unknown>[];
      promotions?: Record<string, unknown>[];
      payment_methods?: Record<string, unknown>[];
      publicationPaymentMethods?: Record<string, unknown>[];
      associatedPaymentMethods?: Record<string, unknown>[];
    };

const getPromotionFromRow = (row: Record<string, unknown>): OnePaymentPromotion | null => {
  const nestedPromotion = row.promotion;
  if (nestedPromotion && typeof nestedPromotion === 'object' && !Array.isArray(nestedPromotion)) {
    const promotion = nestedPromotion as Record<string, unknown>;
    const promotionId = promotion.promotion_id ?? promotion.promotionId ?? promotion.id;
    const name = promotion.name ?? promotion.promotion_name;

    if (promotionId != null && name) {
      return { promotion_id: Number(promotionId), name: String(name) };
    }
  }

  const promotionId = row.promotion_id ?? row.promotionId;
  const name = row.promotion_name ?? row.promotionName ?? row.target_name ?? row.target;

  if (promotionId != null && name) {
    return { promotion_id: Number(promotionId), name: String(name) };
  }

  return null;
};

const parseConfigValues = (value: unknown): Record<string, unknown> | undefined => {
  if (value == null) return undefined;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed != null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : undefined;
    } catch {
      return undefined;
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return undefined;
};

const getPublicationFromRow = (row: Record<string, unknown>): OnePaymentPublication | null => {
  const publicationId = row.publication_id ?? row.publicationId;
  if (publicationId == null) return null;

  return {
    publication_id: publicationId as string | number,
    name: String(row.name ?? row.product_name ?? row.productName ?? '-'),
    sku: row.sku != null ? String(row.sku) : undefined,
    config_values: parseConfigValues(row.config_values),
  };
};

const dedupePromotions = (promotions: OnePaymentPromotion[]): OnePaymentPromotion[] => {
  const uniqueById = new Map<number, OnePaymentPromotion>();

  promotions.forEach((promotion) => {
    if (!uniqueById.has(promotion.promotion_id)) {
      uniqueById.set(promotion.promotion_id, promotion);
    }
  });

  return Array.from(uniqueById.values()).sort((left, right) =>
    left.name.localeCompare(right.name, 'es'),
  );
};

const dedupePublications = (publications: OnePaymentPublication[]): OnePaymentPublication[] => {
  const uniqueById = new Map<string, OnePaymentPublication>();

  publications.forEach((publication) => {
    const key = String(publication.publication_id);
    if (!uniqueById.has(key)) {
      uniqueById.set(key, publication);
    }
  });

  return Array.from(uniqueById.values());
};

const parseRowsResponse = (rows: Record<string, unknown>[]): PublicationPaymentMethodsResult => ({
  publications: dedupePublications(
    rows
      .map(getPublicationFromRow)
      .filter((publication): publication is OnePaymentPublication => publication != null),
  ),
  paymentMethods: dedupePromotions(
    rows
      .map(getPromotionFromRow)
      .filter((promotion): promotion is OnePaymentPromotion => promotion != null),
  ),
});

const mapPromotionRows = (rows: Record<string, unknown>[]): OnePaymentPromotion[] =>
  dedupePromotions(
    rows
      .map((row) => {
        const promotion = getPromotionFromRow(row);
        if (promotion) return promotion;

        const promotionId = row.promotion_id ?? row.promotionId ?? row.id;
        const name = row.name ?? row.promotion_name ?? row.promotionName;

        if (promotionId != null && name) {
          return { promotion_id: Number(promotionId), name: String(name) };
        }

        return null;
      })
      .filter((promotion): promotion is OnePaymentPromotion => promotion != null),
  );

const mapPublicationRows = (rows: Record<string, unknown>[]): OnePaymentPublication[] =>
  dedupePublications(
    rows
      .map(getPublicationFromRow)
      .filter((publication): publication is OnePaymentPublication => publication != null),
  );

export const getPaymentCardId = (card: PaymentCard): number | undefined => {
  const id = card.payment_card_id ?? card.card_id ?? card.id;
  if (id == null || id === '') return undefined;
  const parsed = Number(id);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const getPaymentCardPromotionId = (card: PaymentCard): number | undefined => {
  const id = card.promotion_id ?? card.promotionId;
  if (id == null || id === '') return undefined;
  const parsed = Number(id);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const getPaymentCardLabel = (card: PaymentCard) => {
  const name = card.name ?? card.card_name;
  const fallbackName = [card.brand, card.type].filter(Boolean).join(' ');
  const id = getPaymentCardId(card);

  if (name) return String(name);
  if (fallbackName) return fallbackName;
  return id != null ? `Tarjeta ${id}` : 'Tarjeta sin identificar';
};

export const buildSinglePayOptionValue = (card: PaymentCard): string | null => {
  const promotionId = getPaymentCardPromotionId(card);
  if (promotionId == null) return null;

  return `${getPaymentCardLabel(card)} - ID: ${promotionId}`;
};

export const buildPaymentCardOptionLabel = (card: PaymentCard): string => {
  const promotionId = getPaymentCardPromotionId(card);
  const cardName = getPaymentCardLabel(card);

  if (promotionId != null) {
    return `${cardName} - ID: ${promotionId}`;
  }

  return cardName;
};

export const publicationHasSinglePayOption = (publication: OnePaymentPublication): boolean => {
  const value = publication.config_values?.singlePayOption;

  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;

  return true;
};

export const getConfigCardIds = (configValues?: Record<string, unknown>): string[] => {
  if (!configValues) return [];

  const { card_ids: cardIds, card_id: cardId, singlePayOption } = configValues;

  if (Array.isArray(cardIds) && cardIds.length) {
    return cardIds
      .map((id) => String(id))
      .filter((id) => id !== '' && id !== 'undefined' && id !== 'null');
  }

  if (cardId != null && cardId !== '') {
    return [String(cardId)];
  }

  if (Array.isArray(singlePayOption) && singlePayOption.length) {
    return singlePayOption
      .map((id) => String(id))
      .filter((id) => id !== '' && id !== 'undefined' && id !== 'null');
  }

  if (typeof singlePayOption === 'number') {
    return [String(singlePayOption)];
  }

  return [];
};

export interface PaymentCardOption {
  id: string;
  name: string;
}

export const dedupePaymentCardsById = (cards: PaymentCard[]): PaymentCard[] => {
  const uniqueById = new Map<number, PaymentCard>();

  cards.forEach((card) => {
    const id = getPaymentCardId(card);
    if (id == null || uniqueById.has(id)) return;
    uniqueById.set(id, card);
  });

  return Array.from(uniqueById.values()).sort((left, right) =>
    getPaymentCardLabel(left).localeCompare(getPaymentCardLabel(right), 'es'),
  );
};

export const mapPaymentCardsToOptions = (cards: PaymentCard[]): PaymentCardOption[] =>
  dedupePaymentCardsById(cards)
    .map((card) => {
      const cardId = getPaymentCardId(card);
      if (cardId == null) return null;

      return {
        id: String(cardId),
        name: buildPaymentCardOptionLabel(card),
      };
    })
    .filter((option): option is PaymentCardOption => option != null);

export const fetchPaymentCards = async (): Promise<PaymentCard[]> => {
  const { data } = await AxiosRequest.get<PaymentCardResponse>('/payment-card');

  const rawCards = Array.isArray(data)
    ? data
    : data?.data ?? data?.items ?? data?.paymentCards ?? data?.cards ?? [];

  return dedupePaymentCardsById(rawCards);
};

export const fetchPublicationPaymentMethods = async (
  cardIds: number[],
): Promise<PublicationPaymentMethodsResult> => {
  if (!cardIds.length) {
    return { publications: [], paymentMethods: [] };
  }

  const { data } = await AxiosRequest.get<PublicationPaymentMethodsResponse>(
    '/publication-payment-method',
    {
      params: {
        singlePaymentCards: cardIds.join(','),
      },
    },
  );

  if (Array.isArray(data)) {
    return parseRowsResponse(data);
  }

  const nestedData = data?.data;
  if (nestedData && !Array.isArray(nestedData)) {
    return {
      publications: mapPublicationRows(nestedData.publications ?? []),
      paymentMethods: mapPromotionRows(
        nestedData.paymentMethods ??
          (nestedData as { promotions?: Record<string, unknown>[] }).promotions ??
          [],
      ),
    };
  }

  const promotionRows =
    data?.paymentMethods ??
    data?.promotions ??
    data?.payment_methods ??
    data?.publicationPaymentMethods ??
    data?.associatedPaymentMethods ??
    [];

  return {
    publications: mapPublicationRows(
      data?.publications ?? (Array.isArray(nestedData) ? nestedData : []),
    ),
    paymentMethods: mapPromotionRows(promotionRows),
  };
};
